import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from "react-router-dom";
import { DashboardLayout } from "../components/layout";
import PostCard from "../components/feed/PostCard";
import Avatar from "../components/common/Avatar";
import { Check, Lock, MessageSquare, UserPlus } from "lucide-react";
import CreatePostModal from "../components/post/CreatePostModal";
import api from "../services/api";
import ASSETS from "../data/assets";
import { getAssetUrl } from "../services/helper";
import { toast } from "react-toastify";
import {
  Badgge1,
  Badgge2,
  Badgge3,
  Badgge4,
  Badgge5,
  Badgge6,
  CrownIcon,
  PremiumIcon,
} from "../assets/images/image";
const OtherUserProfile = () => {
  const { id } = useParams();
  const queryClient = useQueryClient();

  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [postPage, setPostPage] = useState(1);
  const [allPosts, setAllPosts] = useState([]); // Accumulate posts  


  // Fetch User Profile
  const {
    data: profileData,
    isLoading: profileLoading,
    error: profileError,
  } = useQuery({
    queryKey: ["other-user-profile", id],
    queryFn: () => api.getUser(id),
    enabled: !!id,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Fetch User's Posts
  // const {
  //   data: postsData,
  //   isLoading: postsLoading,
  // } = useQuery({
  //   queryKey: ["other-user-posts", id],
  //   queryFn: () => api.getPostsByUser(id,pageNo),
  //   enabled: !!id,
  //   staleTime: 3 * 60 * 1000,
  // });

  const {
    data: postsPageData,
    isLoading: isLoadingPosts,
    isFetching: isFetchingMore
  } = useQuery({
    queryKey: ["other-user-posts", id, postPage],
    queryFn: () => api.getPostsByUser(id, postPage),
    enabled: !!id,
    staleTime: 3 * 60 * 1000,
    keepPreviousData: true,
  });

  const newPosts = postsPageData?.posts || [];

  // Append new page to accumulated posts
  useEffect(() => {
    if (newPosts.length > 0) {
      setAllPosts(prev => {
        const existingIds = new Set(prev.map(p => p.id));
        const filteredNew = newPosts.filter(post => !existingIds.has(post.id));
        return [...prev, ...filteredNew];
      });
    }
  }, [newPosts]);

  const hasMore = postsPageData?.pagination?.hasNext || false;

  const loadMorePosts = () => {
    if (!hasMore || isFetchingMore) return;
    setPostPage(prev => prev + 1);
  };


  const followMutation = useMutation({
    mutationFn: (userId) => api.followRequest(userId),
    onSuccess: (userId) => {
      toast.success("Follow Request Submitted")
      // Optimistic update or invalidate
      queryClient.invalidateQueries({ queryKey: ["other-user-profile", id] });
      queryClient.invalidateQueries({ queryKey: ['suggested-pilgrims'] });

      // You could also update local state for immediate UI feedback
    },
    onError: (err) => {
      console.error('Follow failed:', err);
      // Show toast notification here if you have one
    },
  });

  const handleFollow = (userId) => {
    followMutation.mutate(userId);
  };
  const p = profileData?.user || {};

  // const posts = postsData?.posts || [];

  const stats = p?.stats || {};

  const isLoading = profileLoading;

  if (isLoading) {
    return (
      <DashboardLayout onCreatePost={() => setShowCreate(true)}>
        <div className="flex-1 flex items-center justify-center">
          <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (profileError) {
    return (
      <DashboardLayout>
        <div className="flex-1 flex items-center justify-center text-red-500">
          Failed to load profile
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout onCreatePost={() => setShowCreate(true)}>
      <div
        className="flex-1 min-w-0 overflow-y-auto scrollbar-thin px-4 md:px-8 pt-4 sm:pt-6 md:pt-8"
        style={{ maxHeight: "calc(100vh - 56px)" }}
      >
        <div className="relative">
          {/* Cover Image */}
          <div className="h-[90px] sm:h-[150px] md:h-[200px] lg:h-[245px] relative overflow-hidden rounded-t-xl sm:rounded-t-2xl lg:rounded-t-3xl">
            <img
              src={ASSETS.profileCover}
              alt="Cover"
              className="w-full h-full object-cover"
            />
          </div>

          {/* White Section */}
          <div className="bg-white rounded-bl-xl rounded-br-xl px-3 sm:px-6 md:px-8 lg:px-12 relative pt-[45px] sm:pt-[60px] md:pt-[75px] lg:pt-[85px] pb-3 sm:pb-4">
            {/* Avatar */}
            <div className="absolute -top-[32px] sm:-top-[48px] md:-top-[65px] lg:-top-[90px] left-3 sm:left-6 md:left-8 lg:left-12 z-20">
              <div className="w-[70px] h-[70px] sm:w-[105px] sm:h-[105px] md:w-[140px] md:h-[140px] lg:w-[170px] lg:h-[170px] rounded-full overflow-hidden border-4 sm:border-[5px] md:border-[6px] border-white flex-shrink-0">
                <img
                  src={getAssetUrl(p.avatar)}
                  alt={p.name}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Main Content */}
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 sm:gap-6 mb-6 flex-wrap">
              <div className="flex flex-col gap-3 sm:gap-4 w-full lg:w-auto">
                <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                  <div className="text-left flex flex-col gap-1">
                    <p className="text-[18px] sm:text-[22px] md:text-[24px] font-bold text-gray-900 break-words">
                      {p.name}
                    </p>
                    <p className="text-[11px] sm:text-[13px] text-gray-600">
                      Member since {p.member_since || "2024"}
                    </p>
                    <p className="text-[13px] sm:text-[15px] text-gray-700">
                      <span className="font-bold">+{stats.followers || 0}</span> Followers
                    </p>
                  </div>

                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  {(p.isFollowing || p.connectionStatus == 'pending') ? (
                    <button
                      disabled
                      className="px-4 sm:px-5 md:px-6 py-1.5 sm:py-2 md:py-2.5 bg-gray-100 text-gray-600 border border-gray-300 rounded-full font-semibold text-sm sm:text-base cursor-default flex items-center gap-2"
                    >

                      {p.isFollowing ? (<><Check size={18} strokeWidth={2.5} />Following</>) : `Pending Request`}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleFollow(p.id)}
                      disabled={followMutation.isPending}

                      className="px-4 sm:px-5 md:px-6 py-1.5 sm:py-2 md:py-2.5 bg-[#1F3A60] text-white rounded-full font-semibold text-sm sm:text-base hover:bg-[#162B47] transition-all flex items-center gap-2"
                    >
                      <UserPlus size={18} />
                      {followMutation.isPending ? 'Following...' : 'Follow'}
                    </button>
                  )}
                  {p.isFollowing ? (
                    <button
                      onClick={() => navigate(`/messages?chat_id=${p.id}`)}
                      className="px-4 sm:px-5 md:px-6 py-1.5 sm:py-2 md:py-2.5 border border-[#1F3A60] text-[#1F3A60] rounded-full font-semibold text-sm sm:text-base hover:bg-[#1F3A60] hover:text-white transition-all flex items-center gap-2"
                    >
                      <MessageSquare size={18} />
                      Message
                    </button>
                  ) : (
                    <button
                      disabled
                      title="Follow this user to send messages"
                      className="px-4 sm:px-5 md:px-6 py-1.5 sm:py-2 md:py-2.5 border border-gray-300 bg-gray-100 text-gray-400 rounded-full font-semibold text-sm sm:text-base cursor-not-allowed flex items-center gap-2"
                    >
                      <Lock size={18} />
                      Message
                    </button>
                  )}
                </div>
              </div>

              {/* Badge */}
              {/* {p.badge && (
                <div className="flex items-center gap-2 text-center">
                  <h2 className="font-bold text-sm tracking-widest uppercase">
                    {p.badge.toUpperCase()} MEMBER
                  </h2>
                </div>
              )} */}
              <div className="flex flex-col items-start gap-3 flex-shrink-0">
                {p.points > 0 && (<>
                  <p className="text-[17px] font-normal text-[#1A1A1A] font-body">Badges Achieved</p>
                  <div className="flex items-center gap-2">

                    {p.points >= 500 && (
                      <img src={Badgge1} alt="500 Points Badge" className="w-[34.69px] h-[34.69px] object-contain" />
                    )}

                    {p.points >= 1000 && (
                      <img src={Badgge2} alt="1000 Points Badge" className="w-[34.69px] h-[34.69px] object-contain" />
                    )}

                    {p.points >= 2000 && (
                      <img src={Badgge3} alt="2000 Points Badge" className="w-[34.69px] h-[34.69px] object-contain" />
                    )}

                    {p.points >= 5000 && (
                      <img src={Badgge4} alt="5000 Points Badge" className="w-[34.69px] h-[34.69px] object-contain" />
                    )}

                    {p.points >= 10000 && (
                      <img src={Badgge5} alt="10000 Points Badge" className="w-[34.69px] h-[34.69px] object-contain" />
                    )}

                    {p.points >= 20000 && (
                      <img src={Badgge6} alt="20000 Points Badge" className="w-[34.69px] h-[34.69px] object-contain" />
                    )}
                  </div></>)}
                <p className="text-[15.4px] font-normal text-[#657786] font-body">
                  Points Earned: <span className="text-[#657786]">{p.points ?? 0}</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* About Section */}
        <div className="bg-white px-5 sm:px-8 py-5 sm:py-6 rounded-2xl my-4 border border-gray-100 shadow-sm">
          <h3 className="text-[17px] sm:text-[24px] font-bold text-[#0F172A] mb-3 font-body">
            About
          </h3>
          <p className="text-[13px] sm:text-[16px] text-gray-600 leading-[1.75] font-body">
            {p.about_us || "No bio available."}
          </p>

          {/* Mentorship Section */}
          {p.user_role && (
            <div className="border-t-2 border-gray-100 mt-5 pt-5">
              <div className="flex items-center gap-3 mb-3">
                <h4 className="text-[17px] sm:text-[24px] font-semibold text-[#0F172A] font-body">
                  Mentorship
                </h4>
                <span className="px-3 py-0.5 rounded-full bg-gray-100 text-[13px] font-medium text-gray-500 capitalize">
                  {p.user_role}
                </span>
              </div>
            </div>
          )}
          {/* Tags */}
          <div className="flex items-center gap-2 flex-wrap">
            {JSON.parse(p?.interests)?.map((tag) => (
              <span
                key={tag}
                className="text-[12px] sm:text-[13px]   text-[#2b2b2b] border border-gray-200 rounded-full px-6 py-1.5 bg-gray-200 font-body capitalize"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Faith Feed */}
        <div className="flex items-center gap-3 mb-4">
          <span className="text-black text-[16px] md:text-[24px] font-semibold uppercase tracking-wide">
            Faith Feed
          </span>
        </div>

        <>
          {/* Loading State */}
          {isLoadingPosts && allPosts.length === 0 ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : allPosts.length === 0 ? (
            <p className="text-gray-400 py-12 text-center">No posts yet. Create your first one!</p>
          ) : (
            <div class="space-y-4 sm:space-y-5 pb-10 sm:pb-16">
              {allPosts.map((post) => (
                <PostCard key={post.id} post={post} />
              ))}
              {hasMore && (
                <div className="flex justify-center pb-6 mt-10">
                  <button
                    onClick={loadMorePosts}
                    disabled={isFetchingMore}
                    className="px-8 py-3 bg-gray-100 hover:bg-gray-200 rounded-full font-medium text-sm transition-all disabled:opacity-70 flex items-center gap-2"
                  >
                    {isFetchingMore ? (
                      <>
                        <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                        Loading more...
                      </>
                    ) : (
                      "Load More Posts"
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Load More Button */}

        </>
      </div>

      <CreatePostModal isOpen={showCreate} onClose={() => setShowCreate(false)} />
    </DashboardLayout>
  );
};

export default OtherUserProfile;